import React from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Box from '@mui/material/Box';
import '../styles/Accessibility.scss';

interface AccessibilityContent {
  title: string;
  content: string;
}

const Accessibility = () => {
  const { slug } = useParams<{ slug: string }>();

  // Content for each accessibility area
  const accessibilityContent: Record<string, AccessibilityContent> = {
    visual: {
      title: 'Visual Accessibility',
      content: `
        <h2>Style Guide Optimization for Inclusive User Experiences</h2>
        <p>Visual accessibility is not a nice-to-have for us – it's a cornerstone of inclusive digital design. That's why we continuously revise our style guide to ensure that everyone, including users with visual impairments, can interact effortlessly with our content.
          We ensure high contrast between text and background, use scalable font sizes, and provide alt text for all images. We also account for color blindness by incorporating patterns and textures instead of relying on color alone.
        </p>
        <p>Regular accessibility audits and usability tests involving users with visual impairments help us identify and eliminate barriers. We align with established accessibility standards such as the WCAG and keep our style guides up to date to promote inclusivity and seamless experiences for all.
 </p>
      `
    },
    editorial: {
      title: 'Editorial Accessibility',
      content: `
        <h2>Making Media Content Inclusive for Everyone</h2>
        <p>Editorial accessibility is more than a technical requirement – it's our way of showing respect to every user. We ensure that images, graphics, PDFs, and videos are designed to be accessible to people with disabilities.
</p><p>This includes providing accurate, descriptive alt text for all visual content, enabling screen readers to communicate meaning to blind or visually impaired users. Our PDF documents are fully tagged and structured for assistive technologies.
For videos, we offer captions and transcripts to support deaf and hard-of-hearing users, and we add audio descriptions to make visual content accessible for blind viewers.
</p><p>Our goal is clear: all content must be understandable and accessible – with no exceptions. That's how we create a seamless digital experience for everyone.</p>
      `
    },
    usability: {
      title: 'Accessible Usability',
      content: `
        <h2>From One-Dimensional Interfaces to a Multisensory Digital Experience</h2>
        <p>For us, accessible usability goes far beyond user-friendliness. It's about enabling digital experiences for everyone – including individuals with hearing, tactile, or cognitive impairments.</p>
        <p>Our websites and applications are designed to create immersive experiences that extend beyond the visual. We integrate auditory cues, voice control, and haptic feedback to enrich interaction for users who rely on alternative sensory input.
        <p>We don't just tick boxes – we craft inclusive digital spaces that are as diverse as the people who use them.</p>
      `
    },
    technical: {
      title: 'Technical Accessibility',
      content: `
        <h2>Looking Under the Hood</h2>
        <p>We believe that technical accessibility is just as vital as visual and editorial aspects of a website. That's why we build our digital experiences with a solid, accessible code foundation.
</p><p>We use semantic HTML, purposeful ARIA roles, and clean structures to support assistive technologies like screen readers and full keyboard navigation. Beyond structure, we ensure our websites perform reliably across all devices with high responsiveness and stability.
</p><p>Ongoing testing and validation help us stay aligned with evolving accessibility standards (like WCAG) and allow us to quickly address new challenges in an ever-changing digital world.
</p><p>The result? A resilient, accessible infrastructure designed to support every user – today and tomorrow.</p>
      `
    }
  };

  // Get content for the current slug
  const currentContent = accessibilityContent[slug || ''] || {
    title: 'Accessibility Information',
    content: '<p>Select an accessibility area to learn more.</p>'
  };

  return (
    <>
      <Navbar />
      <main role="main" className="main-bg" style={{ height: 'auto' }}>
        <section className="accessibility__content-section">
          <div className="accessibility__container">
            <h1 className="accessibility__title">{currentContent.title}</h1>
            <div className="accessibility__content" dangerouslySetInnerHTML={{ __html: currentContent.content }} />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Accessibility;
